import { useState } from "react";

/* ============================= */
/* LOGIC (TOP)                   */
/* ============================= */

export default function PlanteraPrediction() {
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const fetchPrediction = async (date) => {
    setLoading(true);
    setError("");
    setData(null);

    try {
      const res = await fetch(
        "https://comp-oriented.onrender.com/api/predict",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ target_date: date }),
        }
      );

      const json = await res.json();
      if (!json.success) throw new Error("API failed");

      setData(json.prediction);
    } catch (err) {
      setError("Failed to fetch prediction data");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    fetchPrediction(date);
  };

  /* ============================= */
  /* JSX                          */
  /* ============================= */

  return (
    <>
      <style>{css}</style>

      <section className="plantera-ai">
        <div className="ai-container">
          <div className="ai-header">
            <h2>Plantera Prediction</h2>
            <p>
              AI-powered environmental forecasting using long-term climate and
              sensor intelligence.
            </p>
          </div>

          <div className="ai-grid">
            {/* LEFT */}
            <div className="ai-info">
              <h3>Choose Prediction Date</h3>
              <p>
                Select any date in 2026 to view predicted environmental
                conditions for that day.
              </p>

              <input
                type="date"
                min="2026-01-01"
                max="2026-12-31"
                value={selectedDate}
                onChange={handleDateChange}
                className="date-input"
              />

              {loading && <p style={{ marginTop: "1rem" }}>Loading prediction…</p>}
              {error && (
                <p style={{ marginTop: "1rem", color: "red" }}>{error}</p>
              )}
            </div>

            {/* RIGHT – DATA */}
            <div className="ai-chat">
              <div className="data-wrapper">
                {data ? (
                  <>
                    <div className="stat">
                      <span>MAX TEMPERATURE (°C)</span>
                      <strong>
                        {data.prediction.temperature_2m_max.toFixed(2)}
                      </strong>
                    </div>

                    <div className="stat">
                      <span>MIN TEMPERATURE (°C)</span>
                      <strong>
                        {data.prediction.temperature_2m_min.toFixed(2)}
                      </strong>
                    </div>

                    <div className="stat">
                      <span>HUMIDITY (%)</span>
                      <strong>
                        {data.prediction.relative_humidity_2m_mean.toFixed(2)}
                      </strong>
                    </div>

                    <div className="stat">
                      <span>PRESSURE (hPa)</span>
                      <strong>
                        {data.prediction.pressure_msl_mean.toFixed(2)}
                      </strong>
                    </div>

                    <div className="stat">
                      <span>SOLAR RADIATION</span>
                      <strong>
                        {data.prediction.shortwave_radiation_sum.toFixed(2)}
                      </strong>
                    </div>

                    <p className="note">{data.note}</p>
                  </>
                ) : (
                  <div className="empty-state">
                    Select a date to view predictions.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* ============================= */
/* CSS (UNCHANGED STYLE)         */
/* ============================= */

const css = `
:root{
  --orange:#ff6b2c;
  --offwhite:#fffdf9;
  --border:#f1e4da;
  --text:#2b2b2b;
  --muted:#6b6b6b;
}

*{
  margin:0;
  padding:0;
  box-sizing:border-box;
  font-family:"Segoe UI", system-ui, sans-serif;
}

.plantera-ai{
  width:100%;
  padding:4rem 1.5rem 5rem;
  background:#fffaf5;
}

.ai-container{
  max-width:1100px;
  margin:auto;
  background:white;
  border-radius:24px;
  border:1px solid var(--border);
  box-shadow:0 30px 70px rgba(0,0,0,0.06);
  padding:3rem;
}

.ai-header{
  text-align:center;
  margin-bottom:3rem;
}

.ai-header h2{
  font-size:2.4rem;
  color:var(--orange);
}

.ai-header p{
  color:var(--muted);
}

.ai-grid{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:2.5rem;
}

.ai-info h3{
  font-size:1.4rem;
  margin-bottom:1rem;
}

.ai-info p{
  color:var(--muted);
  margin-bottom:1.2rem;
}

.date-input{
  padding:0.65rem 0.9rem;
  border-radius:10px;
  border:1px solid var(--border);
  width:100%;
}

.date-input:focus{
  outline:none;
  border-color:var(--orange);
}

.ai-chat{
  background:#fffdf9;
  border:1px solid var(--border);
  border-radius:18px;
  padding:1.5rem;
}

.data-wrapper{
  display:flex;
  flex-direction:column;
}

.stat{
  background:#fff7f2;
  border:1px solid var(--border);
  border-radius:16px;
  padding:1.4rem;
  margin-bottom:1rem;
}

.stat span{
  font-size:0.8rem;
  color:var(--muted);
}

.stat strong{
  display:block;
  font-size:1.8rem;
  color:var(--orange);
  margin-top:0.4rem;
}

.note{
  font-size:0.85rem;
  color:var(--muted);
  margin-top:0.5rem;
}

.empty-state{
  color:var(--muted);
  font-size:0.95rem;
  text-align:center;
  padding-top:2rem;
}

@media(max-width:900px){
  .ai-grid{
    grid-template-columns:1fr;
  }
  .ai-container{
    padding:2rem;
  }
}
`;
